'use client';

import { useState, useCallback } from 'react';
import { logger } from '@/lib/logger';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { StripeProvider } from './StripeProvider';
import { PaymentForm } from './PaymentForm';
import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { PAYMENT_AMOUNT } from '@/lib/constants';

interface HirePaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  matchId: string;
  companyId: string;
  amount?: number;
  workerName?: string;
}

type PaymentState = 
  | { type: 'initial' }
  | { type: 'loading' }
  | { type: 'ready'; clientSecret: string; publishableKey: string }
  | { type: 'processing' }
  | { type: 'success' }
  | { type: 'error'; message: string };

export function HirePaymentModal({
  isOpen,
  onClose,
  onSuccess,
  matchId,
  companyId,
  amount = PAYMENT_AMOUNT,
  workerName,
}: HirePaymentModalProps) {
  const [paymentState, setPaymentState] = useState<PaymentState>({ type: 'initial' });
  const supabase = createClient();

  const handleOpen = useCallback(async () => {
    if (paymentState.type !== 'initial') return;

    setPaymentState({ type: 'loading' });

    try {
      const response = await fetch('/api/payments/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          match_id: matchId,
          company_id: companyId,
          amount,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to initialize payment');
      }

      if (!data.clientSecret || !data.publishableKey) {
        throw new Error('Invalid payment configuration');
      }

      setPaymentState({
        type: 'ready',
        clientSecret: data.clientSecret,
        publishableKey: data.publishableKey,
      });
    } catch (error) {
      logger.error('Payment initialization error:', error);
      setPaymentState({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to initialize payment',
      });
    }
  }, [matchId, companyId, amount, paymentState.type]);

  // Initialize payment when modal opens
  if (isOpen && paymentState.type === 'initial') {
    handleOpen();
  }

  const handlePaymentSuccess = () => {
    setPaymentState({ type: 'success' });
    // Call the onSuccess callback after a brief delay to show success state
    setTimeout(() => {
      onSuccess();
      // Reset state for next time
      setPaymentState({ type: 'initial' });
    }, 2000);
  };

  const handleCancel = () => {
    onClose();
    // Reset state after a delay
    setTimeout(() => setPaymentState({ type: 'initial' }), 300);
  };

  const renderContent = () => {
    switch (paymentState.type) {
      case 'initial':
      case 'loading':
        return (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="mt-4 text-muted-foreground">Initializing payment...</p>
          </div>
        );

      case 'ready':
        return (
          <StripeProvider
            publishableKey={paymentState.publishableKey}
            clientSecret={paymentState.clientSecret}
          >
            <PaymentForm
              onSuccess={handlePaymentSuccess}
              onCancel={handleCancel}
              amount={amount}
            />
          </StripeProvider>
        );

      case 'processing':
        return (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="mt-4 text-muted-foreground">Processing payment...</p>
          </div>
        );

      case 'success':
        return (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="rounded-full bg-green-100 p-4">
              <CheckCircle2 className="h-12 w-12 text-green-600" />
            </div>
            <h3 className="mt-4 text-xl font-semibold">Payment Successful!</h3>
            <p className="mt-2 text-center text-muted-foreground">
              The worker has been notified of your hire.
            </p>
          </div>
        );

      case 'error':
        return (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="rounded-full bg-destructive/10 p-4">
              <AlertCircle className="h-12 w-12 text-destructive" />
            </div>
            <h3 className="mt-4 text-xl font-semibold">Payment Failed</h3>
            <p className="mt-2 text-center text-muted-foreground">
              {paymentState.message}
            </p>
            <button
              onClick={() => setPaymentState({ type: 'initial' })}
              className="mt-4 text-primary hover:underline"
            >
              Try again
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {paymentState.type === 'success' 
              ? 'Hire Confirmed' 
              : `Hire${workerName ? ` ${workerName}` : ''}`}
          </DialogTitle>
          {paymentState.type !== 'success' && paymentState.type !== 'error' && (
            <DialogDescription>
              Complete payment to confirm your hire.
            </DialogDescription>
          )}
        </DialogHeader>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}
